import { ref, unref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { getKanbanResolveBlock } from 'dashboard/helper/kanbanResolveBlock';
import wootConstants from 'dashboard/constants/globals';
import ConversationAPI from 'dashboard/api/conversations';

export function useBulkActions() {
  const store = useStore();
  const { t } = useI18n();
  const { checkMissingAttributes } = useConversationRequiredAttributes();

  const selectedConversations = useMapGetter(
    'bulkActions/getSelectedConversationIds'
  );
  const selectedInboxes = ref([]);

  function selectConversation(conversationId, inboxId) {
    store.dispatch('bulkActions/setSelectedConversationIds', conversationId);
    selectedInboxes.value = [...selectedInboxes.value, inboxId];
  }

  function deSelectConversation(conversationId, inboxId) {
    store.dispatch('bulkActions/removeSelectedConversationIds', conversationId);
    // Only remove one instance of the inboxId, not all
    // This handles the case where multiple conversations from the same inbox are selected
    const index = selectedInboxes.value.indexOf(inboxId);
    if (index > -1) {
      selectedInboxes.value = [
        ...selectedInboxes.value.slice(0, index),
        ...selectedInboxes.value.slice(index + 1),
      ];
    }
  }

  function resetBulkActions() {
    store.dispatch('bulkActions/clearSelectedConversationIds');
    selectedInboxes.value = [];
  }

  function selectAllConversations(check, conversationList) {
    const availableConversations = unref(conversationList);
    if (check) {
      store.dispatch(
        'bulkActions/setSelectedConversationIds',
        availableConversations.map(item => item.id)
      );
      selectedInboxes.value = availableConversations.map(item => item.inbox_id);
    } else {
      resetBulkActions();
    }
  }

  function isConversationSelected(id) {
    return selectedConversations.value.includes(id);
  }

  async function updateSingleConversationLabels(conversationId, updateLabels) {
    const labelsResponse = await ConversationAPI.getLabels(conversationId);
    const labels = updateLabels(labelsResponse.data.payload);
    const response = await ConversationAPI.updateLabels(conversationId, labels);
    const updatedLabels = response.data.payload;

    store.dispatch('conversationLabels/setConversationLabel', {
      id: conversationId,
      data: updatedLabels,
    });

    const conversation = store.getters.getConversationById(conversationId);
    if (conversation) {
      store.dispatch('updateConversation', {
        ...conversation,
        labels: updatedLabels,
      });
    }
  }

  // Same method used in context menu, conversationId being passed from there.
  async function onAssignAgent(agent, conversationId = null) {
    const ids = conversationId || selectedConversations.value;

    // Context menu assigns one conversation — use the direct API so the
    // assignee updates immediately without waiting on Sidekiq bulk jobs.
    if (conversationId && ids.length === 1) {
      try {
        await store.dispatch('assignAgent', {
          conversationId: ids[0],
          agentId: agent.id,
          throwOnError: true,
        });
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL', {
            agentName: agent.name,
            conversationId: ids[0],
          })
        );
      } catch (err) {
        useAlert(t('BULK_ACTION.ASSIGN_FAILED'));
      }
      return;
    }

    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids,
        fields: {
          assignee_id: agent.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.ASSIGN_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.ASSIGN_FAILED'));
    }
  }

  // Same method used in context menu, conversationId being passed from there.
  async function onAssignLabels(newLabels, conversationId = null) {
    const ids = conversationId || selectedConversations.value;

    if (conversationId && ids.length === 1) {
      const convId = ids[0];

      try {
        await updateSingleConversationLabels(convId, currentLabels => [
          ...new Set([...currentLabels, ...newLabels]),
        ]);
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL', {
            labelName: newLabels[0],
            conversationId: convId,
          })
        );
      } catch (err) {
        useAlert(t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
      }
      return;
    }

    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids,
        labels: {
          add: newLabels,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
    }
  }

  // Used by both context menu and bulk action bar.
  async function onRemoveLabels(labelsToRemove, conversationId = null) {
    const ids = conversationId || selectedConversations.value;

    if (conversationId && ids.length === 1) {
      const convId = ids[0];

      try {
        await updateSingleConversationLabels(convId, currentLabels =>
          currentLabels.filter(label => !labelsToRemove.includes(label))
        );
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_REMOVAL.SUCCESFUL', {
            labelName: labelsToRemove[0],
            conversationId: convId,
          })
        );
      } catch (err) {
        useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_REMOVAL.FAILED'));
      }
      return;
    }

    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids,
        labels: {
          remove: labelsToRemove,
        },
      });

      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.LABELS.REMOVE_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.LABELS.REMOVE_FAILED'));
    }
  }

  async function onAssignTeamsForBulk(team) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: selectedConversations.value,
        fields: {
          team_id: team.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_FAILED'));
    }
  }

  async function onUpdateConversations(status, snoozedUntil) {
    if (selectedConversations.value.length === 0) return;

    let conversationIds = selectedConversations.value;
    let skippedCount = 0;

    // If resolving, check for required attributes
    if (status === wootConstants.STATUS_TYPE.RESOLVED) {
      const { validIds, skippedIds, blockedCount, missingCount } =
        selectedConversations.value.reduce(
          (acc, id) => {
            const conversation = store.getters.getConversationById(id);
            const { blocked } = getKanbanResolveBlock(conversation);
            if (blocked) {
              acc.skippedIds.push(id);
              acc.blockedCount += 1;
              return acc;
            }

            const currentCustomAttributes =
              conversation?.custom_attributes || {};
            const { hasMissing } = checkMissingAttributes(
              currentCustomAttributes
            );

            if (!hasMissing) {
              acc.validIds.push(id);
            } else {
              acc.skippedIds.push(id);
              acc.missingCount += 1;
            }
            return acc;
          },
          { validIds: [], skippedIds: [], blockedCount: 0, missingCount: 0 }
        );

      conversationIds = validIds;
      skippedCount = skippedIds.length;

      if (skippedCount > 0 && validIds.length === 0) {
        if (blockedCount > 0 && missingCount === 0) {
          useAlert(t('BULK_ACTION.RESOLVE.ALL_BLOCKED_BY_KANBAN'));
        } else if (blockedCount > 0) {
          useAlert(t('BULK_ACTION.RESOLVE.ALL_SKIPPED'));
        } else {
          useAlert(t('BULK_ACTION.RESOLVE.ALL_MISSING_ATTRIBUTES'));
        }
        return;
      }
    }

    try {
      if (conversationIds.length > 0) {
        await store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationIds,
          fields: {
            status,
          },
          snoozed_until: snoozedUntil,
        });
      }

      store.dispatch('bulkActions/clearSelectedConversationIds');

      if (skippedCount > 0) {
        useAlert(t('BULK_ACTION.RESOLVE.PARTIAL_SUCCESS'));
      } else {
        useAlert(t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
    }
  }

  return {
    selectedConversations,
    selectedInboxes,
    selectConversation,
    deSelectConversation,
    selectAllConversations,
    resetBulkActions,
    isConversationSelected,
    onAssignAgent,
    onAssignLabels,
    onRemoveLabels,
    onAssignTeamsForBulk,
    onUpdateConversations,
  };
}
